import { useState } from 'react';
import styled from 'styled-components';

import PaymentCancelInfoModal from './PaymentCancelInfo.modal';
import { convertToKoreanUnit } from '@/util/format';
import PaymentCancelCompleteModal from './PaymentCancelComplete.modal';
import FlexWrap from '../common/FlexWrap';
import { format } from 'date-fns';
import { usePatchCancelPayment } from '@/hooks/usePostCancelPayment';
import { useAppSelector } from '@/hooks/useReduxHooks';
import PaymentCancelModal from './PaymentCancel.modal';
import { getPaymentLogoImage } from '@/util/payment';
import { useQueryClient } from 'react-query';
import PaymentCancelErrorModal from './PaymentCancelError.modal';

type Props = {
  qrOrderId: number;
  cancelAmount?: number;
  isQrOrderCancelled?: null | boolean;
  qrOrderAgent?: string;
  paymentDate?: string;
};

function GlobalPaymentCancelButton({
  qrOrderId,
  cancelAmount,
  isQrOrderCancelled,
  qrOrderAgent,
  paymentDate,
}: Props) {
  const queryClient = useQueryClient();
  const [isPaymentCancelInfoModalOn, setIsPaymentCancelInfoModalOn] =
    useState(false);
  const [isPaymentCancelModalOn, setIsPaymentCancelModalOn] = useState(false);
  const [isPaymentCancelCompleteModalOn, setIsPaymentCancelCompleteModalOn] =
    useState(false);

  const { hospitalId } = useAppSelector((state) => state.user);
  const [isPaymentCancelLoading, setIsPaymentCancelLoading] = useState(false);
  const [isPaymentCancelErrorModalOn, setIsPaymentCancelErrorModalOn] =
    useState(false);
  // 결제 취소 훅 사용
  const { cancelPaymentAsync } = usePatchCancelPayment({
    onSuccess: () => {
      setIsPaymentCancelModalOn(false);
    },
    onError: (error) => {
      console.log('결제 취소 에러:', error);
    },
  });

  const handleOpenDeleteModal = async () => {
    setIsPaymentCancelInfoModalOn(true);
  };

  const handleCloseDeleteModal = () => {
    setIsPaymentCancelInfoModalOn(false);
  };

  // 결제 취소 완료 모달 닫기
  const handleCloseCompleteModal = () => {
    setIsPaymentCancelCompleteModalOn(false);
  };
  // 환불 취소 처리
  const handlePaymentCancel = async () => {
    try {
      setIsPaymentCancelLoading(true);

      await cancelPaymentAsync({
        requestType: 'MEDICAL_USER',
        hospitalId: hospitalId,
        qrOrderIndex: qrOrderId,
      });
      queryClient.invalidateQueries('paymentDetail');
      queryClient.invalidateQueries('refundDetail');
      setIsPaymentCancelCompleteModalOn(true);
    } catch (error) {
      console.error('결제 취소 에러:', error);
    } finally {
      setIsPaymentCancelLoading(false);
    }
  };

  const renderModalContent = () => (
    <PaymentCancelInfoContainer dir='column'>
      <ContentRow>
        <ContentName>거래 일시</ContentName>
        {format(new Date(paymentDate || Date.now()), 'yyyy.MM.dd HH:mm')}
      </ContentRow>
      <ContentRow>
        <ContentName>결제 방법</ContentName>
        <img
          src={getPaymentLogoImage(qrOrderAgent ?? '')}
          alt={`${qrOrderAgent} 로고`}
          height={20}
        />
      </ContentRow>
    </PaymentCancelInfoContainer>
  );

  return (
    <>
      <StyledButton
        onClick={() => {
          handleOpenDeleteModal();
        }}
        isCancelled={isQrOrderCancelled}
        disabled={!!isQrOrderCancelled}
      >
        {isQrOrderCancelled ? '취소 완료' : '결제 취소'}
      </StyledButton>

      {/* 결제 취소 모달 */}
      {isPaymentCancelInfoModalOn && (
        <PaymentCancelInfoModal
          title={'결제가 취소됩니다'}
          cancelAmount={convertToKoreanUnit(Number(cancelAmount))}
          btnTitle='다음'
          subBtnTitle='취소'
          onClickSubBtn={handleCloseDeleteModal}
          onClickBtn={() => {
            setIsPaymentCancelModalOn(true);
            setIsPaymentCancelInfoModalOn(false);
          }}
          contentElement={renderModalContent()}
        />
      )}
      <PaymentCancelModal
        isOpen={isPaymentCancelModalOn}
        title={'결제 취소하기'}
        desc={'결제를 취소할까요?'}
        subBtnTitle='취소'
        onClickSubBtn={() => {
          setIsPaymentCancelModalOn(false);
        }}
        btnTitle='결제 취소하기'
        isBtnLoading={isPaymentCancelLoading}
        onClickBtn={handlePaymentCancel}
      />
      <PaymentCancelErrorModal
        isOpen={isPaymentCancelErrorModalOn}
        desc={
          '해당 건은 환급금 수령이 완료되어 직접 취소가 불가합니다.\n취소를 원하시면 채팅 혹은 전화로 문의 부탁드립니다.'
        }
        onClose={() => {
          setIsPaymentCancelErrorModalOn(false);
        }}
      />
      {isPaymentCancelCompleteModalOn && (
        <PaymentCancelCompleteModal
          title='결제가 취소되었습니다'
          cancelDate={format(new Date(), 'yyyy.MM.dd HH:mm')}
          cancelAmount={convertToKoreanUnit(Number(cancelAmount))}
          paymentDate={format(
            new Date(paymentDate || Date.now()),
            'yyyy.MM.dd HH:mm',
          )}
          paymentMethod={qrOrderAgent}
          btnTitle='확인'
          onClickBtn={handleCloseCompleteModal}
        />
      )}
    </>
  );
}

const StyledButton = styled.button<{ isCancelled?: null | boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 67px;
  height: 32px;

  white-space: nowrap;
  color: #80848a;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;

  border-radius: 120px;
  border: 1px solid #cccccc;

  background-color: #fafafa;
  &:not(:disabled):hover {
    border-color: #a7c4fb;
    background-color: #d3e2fd;
  }
  ${({ isCancelled }) =>
    isCancelled === true &&
    `
      color: #CBCCCE;
      border: none;
      cursor: not-allowed;
  `}
`;
const PaymentCancelInfoContainer = styled(FlexWrap)`
  padding-top: 24px;
  gap: 12px;
`;

const ContentRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  color: #3a3b3e;
  text-align: right;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

const ContentName = styled.div`
  color: #80848a;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;

export default GlobalPaymentCancelButton;
